import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { GetPackagesByCustomerId } from "api/packages";
import FlexBetween from "components/FlexBetween";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  ModeEditOutlineOutlined,
  Search,
  PersonOutlined,
  PhoneAndroidOutlined,
  LocationOnOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  FilterListOutlined,
} from "@mui/icons-material";
import PackageEditDialog from "components/PackageEditDialog";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { user } from "localStorage";
import { useParams } from "react-router-dom";
import { GetCustomerInfo } from "api/customers";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import PageShell from "components/bento/PageShell";
import StyledDataGrid from "components/bento/StyledDataGrid";
import {
  BG_WHITE,
  BORDER,
  RADIUS_TILE,
  CARD_SHADOW,
  PURPLE_MAIN,
  PURPLE_LIGHT,
  TEXT_SUBTLE,
  DIVIDER,
} from "components/bento/bentoTokens";

const statusList = ["All", "Pending", "Arrived", "Delivered", "END"];

const statusColor = {
  Pending: "info",
  Arrived: "warning",
  Delivered: "success",
  END: "default",
};

function CustomerInfoCard({ customer, id }) {
  if (!customer) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          border: `1px solid ${BORDER}`,
          borderRadius: RADIUS_TILE,
          boxShadow: CARD_SHADOW,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary" variant="body2">
          Loading customer info...
        </Typography>
      </Paper>
    );
  }

  const isActive = customer.validflag;
  const firstLetter = (customer.name || "?").charAt(0).toUpperCase();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        border: `1px solid ${BORDER}`,
        borderRadius: RADIUS_TILE,
        boxShadow: CARD_SHADOW,
        background: BG_WHITE,
      }}
    >
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {/* Avatar */}
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: PURPLE_MAIN,
            fontSize: "1.3rem",
            fontWeight: 700,
            color: "white",
            flexShrink: 0,
          }}
        >
          {firstLetter}
        </Avatar>

        {/* Info */}
        <Box flexGrow={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" fontWeight={700} noWrap>
              {customer.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              #{id}
            </Typography>
            <Chip
              size="small"
              icon={
                isActive ? (
                  <CheckCircleOutlined sx={{ fontSize: "14px !important" }} />
                ) : (
                  <CancelOutlined sx={{ fontSize: "14px !important" }} />
                )
              }
              label={isActive ? "Active" : "Inactive"}
              color={isActive ? "success" : "default"}
              variant="outlined"
            />
          </Box>

          <Box
            display="flex"
            gap={3}
            mt={0.75}
            flexWrap="wrap"
          >
            {customer.whatsapp && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PhoneAndroidOutlined
                  sx={{ fontSize: 15, color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {customer.whatsapp}
                </Typography>
              </Box>
            )}
            {customer.province && customer.district && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationOnOutlined
                  sx={{ fontSize: 15, color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {customer.province.pr_name}, {customer.district.dt_name}
                </Typography>
              </Box>
            )}
            {customer.email && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PersonOutlined sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {customer.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function FilterBar({ status, setStatus, searchInput, setSearchInput, setSearch }) {
  const timerRef = useRef(null);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSearch(val), 600);
  };

  return (
    <Box
      display="flex"
      gap={1.5}
      mb={2}
      flexWrap="wrap"
      alignItems="center"
    >
      <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
        <FilterListOutlined fontSize="small" />
        <Typography variant="body2" fontWeight={500}>
          Filter
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search tracking, order ID, description..."
        value={searchInput}
        onChange={(e) => handleSearchChange(e.target.value)}
        sx={{ minWidth: 260, flexGrow: 1, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Status</InputLabel>
        <Select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {statusList.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

function CustomerInfo() {
  const { id } = useParams();
  const theme = useTheme();
  const [customer, setCustomer] = useState(null);

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("Arrived");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState({});

  const [packageData, setPackageData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");

  const _user = user();
  const isAgentUser = _user.role === "agent";

  useEffect(() => {
    GetCustomerInfo(id)
      .then((res) => {
        if (res && res.success) setCustomer(res.data);
      })
      .catch(console.log);
  }, [id]);

  const fetchPackages = useCallback(
    (page, pageSize, sort, search) => {
      setIsLoading(true);
      GetPackagesByCustomerId(id, page, pageSize, sort, search)
        .then((res) => {
          if (res.status && res.status === 200) {
            if (isMobile && page > 0) {
              setRows((prev) => {
                const newRows = res.data.filter(
                  (n) => !prev.some((e) => e._id === n._id)
                );
                return [...prev, ...newRows];
              });
            } else {
              setRows(res.data);
            }
            setTotalCount(res.total || res.data.length);
          } else {
            Swal.fire(`Error - ${res.status}`, res.message || res, "error");
          }
        })
        .finally(() => setIsLoading(false))
        .catch((err) => {
          setIsLoading(false);
          console.log(err);
        });
    },
    [id]
  );

  // Infinite scroll for mobile
  useEffect(() => {
    if (!isMobile) return;
    const container = document.getElementById("main-scroll-container") || window;
    const handleScroll = () => {
      const el = /** @type {Element} */ (container);
      const scrollTop    = container === window ? window.scrollY     : el.scrollTop;
      const clientHeight = container === window ? window.innerHeight : el.clientHeight;
      const scrollHeight = container === window ? document.documentElement.scrollHeight : el.scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - 200 && !isLoading && rows.length < totalCount) {
        setPage((p) => p + 1);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isLoading, rows.length, totalCount]);

  useEffect(() => {
    fetchPackages(page, pageSize, sort, search);
  }, [fetchPackages, page, pageSize, sort, search, id]);

  useEffect(() => {
    if (isMobile) {
      setRows([]);
      setPage(0);
    }
  }, [search]);

  // Client-side status filter for mobile
  useEffect(() => {
    if (!isMobile) return;
    let filtered =
      status === "All" ? rows : rows.filter((r) => r.status === status);
    if (search) {
      filtered = filtered.filter(
        (r) =>
          r.tracking?.toLowerCase().includes(search.toLowerCase()) ||
          r.description?.toLowerCase().includes(search.toLowerCase()) ||
          r.orderId?.toLowerCase().includes(search.toLowerCase())
      );
    }
    setFilteredRows(filtered);
  }, [rows, status, search]);

  const buttonEditHandler = (rowValue) => {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  };

  const columns = [
    { field: "_id", headerName: "ID", flex: 1, hide: true },
    { field: "orderId", headerName: "Order ID", flex: 1, hide: isMobile },
    {
      field: "tracking",
      headerName: "Tracking",
      flex: 1,
      renderCell: (cell) => (
        <Typography
          variant="body2"
          color="secondary"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            setTrackingNumber(cell.row.tracking);
            setTrackingDialogOpen(true);
          }}
        >
          {cell.row.tracking}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 3,
      renderCell: (cell) =>
        cell.row.description ? (
          cell.row.description
        ) : cell.row.remark ? (
          <Typography color="secondary">Remark: {cell.row.remark}</Typography>
        ) : (
          "-"
        ),
    },
    { field: "routeId", headerName: "Route ID", flex: 0.5, hide: isMobile },
    { field: "shippingFee", headerName: "Shipping Fee", flex: 0.5 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      hide: isMobile,
      renderCell: (cell) => (
        <Chip
          size="small"
          label={cell.row.status}
          color={statusColor[cell.row.status] || "default"}
          variant="outlined"
        />
      ),
    },
    { field: "station", headerName: "Station", flex: 0.7, hide: isMobile },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      hide: isMobile,
      renderCell: (cell) =>
        cell.row.updatedAt
          ? moment(cell.row.updatedAt)
              .tz("Asia/Vientiane")
              .format("DD/MM/YYYY, HH:mm:ss")
          : "-",
    },
    {
      field: "edit",
      headerName: "",
      flex: 0.2,
      sortable: false,
      renderCell: (cell) => (
        <IconButton
          size="small"
          onClick={() => buttonEditHandler(cell.row)}
          sx={{ "&:hover": { color: theme.palette.warning.main } }}
        >
          <ModeEditOutlineOutlined fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <PageShell title="Customer Info">
      <CustomerInfoCard customer={customer} id={id} />

      <FilterBar
        status={status}
        setStatus={setStatus}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        setSearch={(val) => {
          setSearch(val);
          if (!isMobile) {
            setPage(0);
          }
        }}
      />

      {!isMobile ? (
        <Box height="calc(100vh - 320px)" minHeight={400}>
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id}
            rows={rows || []}
            columns={columns}
            rowCount={totalCount || 0}
            rowsPerPageOptions={[20, 50, 100]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode="server"
            sortingMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            onSortModelChange={(newSortModel) => setSort(...newSortModel)}
            components={{ Toolbar: DataGridCustomToolbar }}
            componentsProps={{
              toolbar: { searchInput, setSearchInput, setSearch },
            }}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </Box>
      ) : (
        <Box>
          {/* Initial loading */}
          {isLoading && filteredRows.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={300}
              gap={2}
            >
              <CircularProgress size={40} />
              <Typography variant="body1" color="text.secondary">
                Loading packages...
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={1.5}>
                {filteredRows.map((pkg, index) => (
                  <Grid item key={pkg._id} xs={12} md={6} xl={4}>
                    <Card sx={{ background: BG_WHITE, border: `1px solid ${BORDER}`, borderRadius: RADIUS_TILE, boxShadow: CARD_SHADOW }}>
                      <CardHeader
                        onClick={() => {
                          setTrackingNumber(pkg.tracking);
                          setTrackingDialogOpen(true);
                        }}
                        sx={{ cursor: "pointer" }}
                        avatar={
                          <Avatar
                            sx={{
                              bgcolor:
                                parseInt(pkg.amount) !== 0
                                  ? PURPLE_LIGHT
                                  : TEXT_SUBTLE,
                            }}
                          >
                            {isAgentUser
                              ? parseInt(pkg.amount) +
                                parseInt(
                                  pkg.finalShippingFee || pkg.shippingFee || 0
                                )
                              : parseInt(pkg.amount) +
                                parseInt(pkg.shippingFee || 0)}
                          </Avatar>
                        }
                        title={
                          <Typography variant="subtitle1" color="secondary" fontWeight={600}>
                            {typeof pkg.tracking === "string"
                              ? pkg.tracking
                              : JSON.stringify(pkg.tracking)}
                          </Typography>
                        }
                        subheader={moment(pkg.updatedAt)
                          .tz("Asia/Vientiane")
                          .format("DD/MM/YYYY, HH:mm:ss")}
                        action={
                          <Chip
                            size="small"
                            label={pkg.status}
                            color={statusColor[pkg.status] || "default"}
                            variant="outlined"
                            sx={{ mt: 1, mr: 1 }}
                          />
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <FlexBetween>
                          <Typography variant="body2" color="text.secondary">
                            Order:{" "}
                            {typeof pkg.orderId === "string"
                              ? pkg.orderId
                              : "NA"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {typeof pkg.station === "string" ? pkg.station : ""}{" "}
                            ({typeof pkg.routeId === "string" ? pkg.routeId : ""}
                            )
                          </Typography>
                        </FlexBetween>
                        {pkg.description && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {typeof pkg.description === "string"
                              ? pkg.description
                              : ""}
                          </Typography>
                        )}
                        {pkg.remark && (
                          <Typography
                            variant="body2"
                            color="secondary"
                          >
                            Remark:{" "}
                            {typeof pkg.remark === "string" ? pkg.remark : ""}
                          </Typography>
                        )}
                        {pkg.deliveryAddress && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Delivery:{" "}
                            {typeof pkg.deliveryAddress === "string"
                              ? pkg.deliveryAddress
                              : ""}
                          </Typography>
                        )}
                      </CardContent>
                      <Divider sx={{ borderColor: DIVIDER }} />
                      <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => buttonEditHandler(pkg)}
                          sx={{ "&:hover": { color: theme.palette.warning.main } }}
                        >
                          <ModeEditOutlineOutlined fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {isLoading && filteredRows.length > 0 && (
                <Box display="flex" justifyContent="center" py={3} gap={1}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more...
                  </Typography>
                </Box>
              )}

              {!isLoading && rows.length >= totalCount && filteredRows.length > 0 && (
                <Box display="flex" justifyContent="center" py={2}>
                  <Typography variant="body2" color="text.secondary">
                    All {totalCount} packages loaded
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <PackageEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        packageData={packageData}
        rows={rows}
        setRows={setRows}
        isAgentUser={isAgentUser}
      />
      <TrackingDetailDialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        trackingNumber={trackingNumber}
      />
    </PageShell>
  );
}

export default CustomerInfo;
